import axios from "axios";

import PaymentApi from "shared/api/paymentAPI";
import { getCampaignCustomFields } from "shared/api/micrositeCampaignCustomFieldsAPI";
import { concat, pick } from "ramda";

import {
  CAMPAIGNS_LIST_API,
  DONATIONS_V2_API,
  CREATE_ABANDON_FORM_API,
  SRPEEDLY_CHALLENGE_SUCCESS_API,
  MATCHING_GIFTS_API,
  CAMPAIGNS_STATUS_API,
  DONATIONS_EVERYDAY_FORM_V2_API,
} from "../api";

import {
  addToCart,
  addAmountToCart,
  clearCart,
  fetchCart,
  removeFromCart,
} from "../lib/utils/persistent-cart";

import { saveDoubleDonation } from "./../shared/services/DoubleDonationService";
import { buildGivingDonationData } from "shared/components/MatchingGifts/utils/utils";
import { debounce } from "lodash";
import MixpanelService from "shared/services/MixpanelService";

const prefix = concat("CAMPAIGN:");

const FETCH_SUCCESS = prefix("FETCH_SUCCESS");
const FETCH_ERROR = prefix("FETCH_ERROR");

const SET_LOADING_DONE = prefix("SET_LOADING_DONE");
const SET_LOADING = prefix("SET_LOADING");
const SET_GIVE_BUTTON_DISABLE = prefix("SET_GIVE_BUTTON_DISABLE");

const SET_AMBASSADORS = prefix("SET_AMBASSADORS");
const SET_SELECTED_AMBASSADOR = prefix("SET_SELECTED_AMBASSADOR");

const SET_START_3DS_FLOW = prefix("SET_START_3DS_FLOW");
const PUSH_SELECTED_CAMPAIGN = prefix("PUSH_SELECT_CAMPAIGN");
const REMOVE_SELECTED_CAMPAIGN = prefix("REMOVE_SELECT_CAMPAIGN");
const RESET_CAMPAIGN_SELECTIONS = prefix("RESET_CAMPAIGN_SELECTIONS");
const RESET_CAMPAIGN_FETCH = prefix("RESET_CAMPAIGN_FETCH");
const NEXT_PAGE = prefix("NEXT_PAGE");
const UPDATE_SEARCH = prefix("UPDATE_SEARCH");
const RESET_ADDITIONAL_GIFTS = prefix("RESET_ADDITIONAL_GIFTS");
const SUBMIT_DONATION_ERROR = prefix("SUBMIT_DONATION_ERROR");
const SUBMIT_DONATION_SUCCESS = prefix("SUBMIT_DONATION_SUCCESS");
const SET_CAMPAIGN_DONATE_AMOUNT = prefix("SET_CAMPAIGN_DONATE_AMOUNT");
const SET_ADDITIONAL_GIFT_AMOUNT = prefix("SET_ADDITIONAL_GIFT_AMOUNT");
const SET_GIVING_STEP = prefix("SET_GIVING_STEP");
const SET_DONATE_PAYMENT_VALUES = prefix("SET_DONATE_PAYMENT_VALUES");
const SET_TOTAL_AMOUNT = prefix("SET_TOTAL_AMOUNT");
const SET_DONATION_LEVEL_ID = prefix("SET_DONATION_LEVEL_ID");
const SET_DONATION_ERROR = prefix("SET_DONATION_ERROR");
const SET_FEE_AMOUNT = prefix("SET_FEE_AMOUNT");
const SET_SPREEDLY_MONTH = prefix("SET_SPREEDLY_MONTH");
const SET_SPREEDLY_YEAR = prefix("SET_SPREEDLY_YEAR");
const SET_SPREEDLY_KEY = prefix("SET_SPREEDLY_KEY");
const SET_SPREEDLY_TOKEN = prefix("SET_SPREEDLY_TOKEN");
const SET_PAYMENT_SUBMIT = prefix("SET_PAYMENT_SUBMIT");
const SET_FINAL_CAMPAIGNS_LIST = prefix("SET_FINAL_CAMPAIGNS_LIST");
const SET_STRIPE_CLIENT_SECRET = prefix("SET_STRIPE_CLIENT_SECRET");
const SET_STRIPE_SETUP_INTENT = prefix("SET_STRIPE_SETUP_INTENT");
const SET_STRIPE_PAYMENT_INTENT = prefix("SET_STRIPE_PAYMENT_INTENT");
const SET_BRAINTREE_CLIENT_DATA = prefix("SET_BRAINTREE_CLIENT_DATA");
const SET_BRAINTREE_PAYMENT_DATA = prefix("SET_BRAINTREE_PAYMENT_DATA");
const SET_BBMS_CLIENT_DATA = prefix("SET_BBMS_CLIENT_DATA");
const SET_BBMS_TRANSACTION_TOKEN = prefix("SET_BBMS_TRANSACTION_TOKEN");
const SET_DAF_AMOUNT = prefix("SET_DAF_AMOUNT");
const SET_DAF_WORKFLOW_SESSION_ID = prefix("SET_DAF_WORKFLOW_SESSION_ID");
const SET_CATEGORY = prefix("SET_CATEGORY");
const SET_MATCH_ID = prefix("SET_MATCH_ID");
const SET_SUBSCRIPTION_VALUES = prefix("SET_SUBSCRIPTION_VALUES");
const SET_SINGLE_GIVE = prefix("SET_SINGLE_GIVE");
const SET_CAUSES_ALLOCATION = prefix("SET_CAUSES_ALLOCATION");
const SET_ADDED_ADDITIONAL_GIFT = prefix("SET_ADDED_ADDITIONAL_GIFT");
const SET_IS_GIFT_AID = prefix("SET_IS_GIFT_AID");
const SET_IS_ABANDON_FORM = prefix("SET_IS_ABANDON_FORM");
const SET_EVERYDAY_FORM_CAMPAIGNS_TOTAL = prefix("SET_EVERYDAY_FORM_CAMPAIGNS_TOTAL");
const SET_DD_COMPANY_ID = prefix("SET_DD_COMPANY_ID");
const SET_DOUBLE_DONATION = prefix("SET_DOUBLE_DONATION");
const SET_SHOW_CAMPAIGN_AMOUNT_SCREEN = prefix("SET_SHOW_CAMPAIGN_AMOUNT_SCREEN");
const SET_CURRENT_CAMPAIGN = prefix("SET_CURRENT_CAMPAIGN");
const SET_SHOW_GIVING_TO_SELECTED_FUNDS_SCREEN = prefix(
  "SET_SHOW_GIVING_TO_SELECTED_FUNDS_SCREEN",
);
const SET_PAYMENT_ID = prefix("SET_PAYMENT_ID");
const UPDATE_SELECTED_CAMPAIGN_CUSTOM_FIELDS = prefix("UPDATE_SELECTED_CAMPAIGN_CUSTOM_FIELDS");
const SET_RESOLVED_CUSTOM_FIELDS = prefix("SET_RESOLVED_CUSTOM_FIELDS");

const initialFormValues = [
  "campaignData",
  "currentCampaign",
  "causesAllocation",
  "donationError",
  "currentFormObjectDonatePayment",
  "resolvedCustomFields",
  "spreedlyToken",
  "spreedlyKey",
  "spreedlyMonth",
  "spreedlyYear",
  "finalCampaignsList",
  "paymentSubmit",
  "stripePaymentIntent",
  "subscriptionValues",
  "braintreePaymentData",
  "bbmsTransactionToken",
  "search",
  "category",
  "page",
];

const cartKey = "givingV2:selectedCampaigns:v2";

const cachedCampaigns = fetchCart(cartKey);

const INITIAL_STATE = {
  search: "",
  page: 1,
  limit: 10,
  totalCount: 0,
  hasMoreItems: true,
  campaignData: [],
  campaignListVersion: 0,
  causesAllocation: [],
  selectedCampaigns: cachedCampaigns,
  showCampaignAmountScreen: false,
  showGivingToSelectedFundsScreen: false,
  currentCampaign: {},
  finalCampaignsList: [],
  loading: false,
  currentFormObjectDonatePayment: {},
  resolvedCustomFields: [],
  totalAmount: 0,
  campaignCategories: [],
  category: null,
  isSingleGive: "",
  spreedlyKey: "",
  spreedlyMonth: "",
  spreedlyYear: "",
  spreedlyToken: "",
  stripeClientSecret: "",
  featuresEnabled: [],
  centralizedGifts: [],
  currentStep: "givingSelectionStep",
  donationError: "",
  feeAmount: 0,
  paymentSubmit: "",
  stripeSetupIntent: {},
  stripePaymentIntent: {},
  braintreePaymentData: {},
  braintreeClientData: {},
  bbmsClientData: {},
  subscriptionValues: {},
  bbmsTransactionToken: "",
  isGiftAid: false,
  isAbandonForm: false,
  start3dsFlow: false,
  donationLevelId: "",
  doubleDonation: {},
  matchId: "",
  ambassadors: [],
  selectedAmbassador: null,
  paymentId: "",
  pendingSettlementNotice: false,
};

// Action Creators
export const submitPaypalPaymentApi = (
  entityId,
  sectionId,
  currentFormObjectRegistration,
  everydayForm,
  paypalPayment,
) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (paypalPayment) {
      dispatch(setFeeAmount(0));

      const formData = buildGivingDonationData(
        entityId,
        state.givingFormV2.currentFormObjectDonatePayment,
        currentFormObjectRegistration,
        sectionId,
        "paypal",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        state.givingFormV2.selectedCampaigns,
        0,
        state.givingFormV2.isGiftAid,
        state.givingFormV2.subscriptionValues,
        state.givingFormV2.causesAllocation,
        state.givingFormV2.donationLevelId,
        state.givingFormV2.dDCompanyId,
        null,
        paypalPayment,
        everydayForm,
      );

      // Submit formData to API
      return dispatch(submitDonationApi(formData));
    }
  };
};

export const setAmbassadors = (ambassadors) => ({
  type: SET_AMBASSADORS,
  payload: { ambassadors },
});

export const setSelectedAmbassador = (ambassador) => ({
  type: SET_SELECTED_AMBASSADOR,
  payload: { ambassador },
});

export const setEverydayFormCampaignsTotal = (count) => ({
  type: SET_EVERYDAY_FORM_CAMPAIGNS_TOTAL,
  payload: count,
});

export const fetchAmbassadors = (entityId) => {
  return async (dispatch) => {
    try {
      const response = await axios.get(`/api/v2/ambassadors`, {
        params: { entity_id: entityId },
      });
      dispatch(setAmbassadors(response.data));
    } catch (error) {
      console.error("Error fetching ambassadors:", error);
    }
  };
};

export const setGivingToSelectedFundsScreen = (bool) => ({
  type: SET_SHOW_GIVING_TO_SELECTED_FUNDS_SCREEN,
  payload: { bool },
});

export const setCurrentCampaign = (campaign) => ({
  type: SET_CURRENT_CAMPAIGN,
  payload: { campaign },
});

export const setShowCampaignAmountScreen = (bool) => ({
  type: SET_SHOW_CAMPAIGN_AMOUNT_SCREEN,
  payload: { bool },
});

export const setStripeSetupIntent = (setupIntent) => ({
  type: SET_STRIPE_SETUP_INTENT,
  payload: { setupIntent },
});

export const setDoubleDonation = (dd) => ({
  type: SET_DOUBLE_DONATION,
  payload: { dd },
});

export const setDDCompanyId = (id) => ({
  type: SET_DD_COMPANY_ID,
  payload: { id },
});

export const setIsAbandonForm = (bool) => ({
  type: SET_IS_ABANDON_FORM,
  payload: { bool },
});

export const setStart3dsFlow = (bool) => ({
  type: SET_START_3DS_FLOW,
  payload: { bool },
});

export const setIsGiftAid = (bool) => ({
  type: SET_IS_GIFT_AID,
  payload: { bool },
});

export const setAddedAdditionalGift = (bool) => ({
  type: SET_ADDED_ADDITIONAL_GIFT,
  payload: { bool },
});

export const setCausesAllocation = (causes) => ({
  type: SET_CAUSES_ALLOCATION,
  payload: { causes },
});

export const setSingleGive = (isSingleGive) => ({
  type: SET_SINGLE_GIVE,
  payload: { isSingleGive },
});

export const setSubscriptionValues = (isSubscription, periodLength, interval) => ({
  type: SET_SUBSCRIPTION_VALUES,
  payload: { isSubscription, periodLength, interval },
});

export const setCategory = (category) => ({
  type: SET_CATEGORY,
  payload: { category },
});

export const setMatchId = (matchId) => ({
  type: SET_MATCH_ID,
  payload: { matchId },
});

export const setSpreedlyKey = (key) => ({
  type: SET_SPREEDLY_KEY,
  payload: { key },
});

export const setSpreedlyMonth = (month) => ({
  type: SET_SPREEDLY_MONTH,
  payload: { month },
});

export const setSpreedlyYear = (year) => ({
  type: SET_SPREEDLY_YEAR,
  payload: { year },
});

export const setBbmsTransactionToken = (token) => ({
  type: SET_BBMS_TRANSACTION_TOKEN,
  payload: { token },
});

export const setDafAmount = (amount) => ({
  type: SET_DAF_AMOUNT,
  payload: { amount },
});

export const setDafWorkflowSessionId = (workflowSessionId) => ({
  type: SET_DAF_WORKFLOW_SESSION_ID,
  payload: { workflowSessionId },
});

export const setBbmsClientData = (clientData) => ({
  type: SET_BBMS_CLIENT_DATA,
  payload: { clientData },
});

export const setBraintreeClientData = (clientData) => ({
  type: SET_BRAINTREE_CLIENT_DATA,
  payload: { clientData },
});

export const setBraintreePaymentData = (paymentData) => ({
  type: SET_BRAINTREE_PAYMENT_DATA,
  payload: { paymentData },
});

export const setStripePaymentIntent = (paymentIntent) => ({
  type: SET_STRIPE_PAYMENT_INTENT,
  payload: { paymentIntent },
});

export const setPaymentSubmit = (step) => ({
  type: SET_PAYMENT_SUBMIT,
  payload: { step },
});

export const setStripeClientSecret = (secret) => ({
  type: SET_STRIPE_CLIENT_SECRET,
  payload: { secret },
});

export const setSpreedlyToken = (token) => ({
  type: SET_SPREEDLY_TOKEN,
  payload: { token },
});

export const setDonatePaymentValues = (values) => ({
  type: SET_DONATE_PAYMENT_VALUES,
  payload: { values },
});

export const setResolvedCustomFields = (fields) => ({
  type: SET_RESOLVED_CUSTOM_FIELDS,
  payload: { fields },
});

export const setTotalAmount = (amount) => ({
  type: SET_TOTAL_AMOUNT,
  payload: { amount },
});

export const setDonationLevelId = (id) => ({
  type: SET_DONATION_LEVEL_ID,
  payload: { id },
});

export const setGivingStep = (step) => ({
  type: SET_GIVING_STEP,
  payload: { step },
});

export const setLoading = () => ({
  type: SET_LOADING,
});

export const setLoadingDone = () => ({
  type: SET_LOADING_DONE,
});

export const setGiveButtonDisable = (isDisable) => ({
  type: SET_GIVE_BUTTON_DISABLE,
  payload: { isDisable },
});

export const fetchSuccess = (response) => ({
  type: FETCH_SUCCESS,
  payload: { response },
});

export const fetchError = (error) => ({
  type: FETCH_ERROR,
  payload: { error },
});

export const setFinalCampaignsList = (campaigns) => ({
  type: SET_FINAL_CAMPAIGNS_LIST,
  payload: { campaigns },
});

export const pushSelectedCampaign = (campaign, entityId, everydayFormId) => {
  /* Preventing the storage of selected funds in localstorage for everydayForm*/
  if (!everydayFormId) {
    addToCart(cartKey, campaign, entityId);
  }

  return {
    type: PUSH_SELECTED_CAMPAIGN,
    payload: { campaign },
  };
};

export const removeSelectedCampaign = (id) => {
  removeFromCart(cartKey, id);

  return {
    type: REMOVE_SELECTED_CAMPAIGN,
    payload: { id },
  };
};

export const resetCampaignSelections = () => ({
  type: RESET_CAMPAIGN_SELECTIONS,
});

export const resetAdditionalGiftSelections = () => ({
  type: RESET_ADDITIONAL_GIFTS,
});

export const setAdditionalGiftAmount = (campaignId, amount) => ({
  type: SET_ADDITIONAL_GIFT_AMOUNT,
  payload: { campaignId, amount },
});

export const resetCampaignFetch = () => ({
  type: RESET_CAMPAIGN_FETCH,
});

export const updateSearch = (searchTerm) => ({
  type: UPDATE_SEARCH,
  payload: { searchTerm },
});

export const nextPage = () => ({
  type: NEXT_PAGE,
});

export const setCampaignDonateAmount = (campaignId, amount) => {
  addAmountToCart(cartKey, campaignId, amount);

  return {
    type: SET_CAMPAIGN_DONATE_AMOUNT,
    payload: { campaignId, amount },
  };
};

export const submitDonationError = (error) => ({
  type: SUBMIT_DONATION_ERROR,
  payload: { error },
});

export const setDonationError = (error) => ({
  type: SET_DONATION_ERROR,
  payload: { error },
});

export const setFeeAmount = (amount) => ({
  type: SET_FEE_AMOUNT,
  payload: { amount },
});

export const setPaymentId = (paymentId) => ({
  type: SET_PAYMENT_ID,
  payload: { paymentId },
});

export const updateSelectedCampaignCustomFields = (campaignData) => ({
  type: UPDATE_SELECTED_CAMPAIGN_CUSTOM_FIELDS,
  payload: { campaignData },
});

export const loadSelectedCampaignCustomFields = () => {
  return async (dispatch, getState) => {
    const selectedCampaigns = getState().givingFormV2.selectedCampaigns || [];
    const campaignsToLoad = selectedCampaigns.filter(
      (campaign) => campaign.custom_fields_loaded !== true,
    );

    if (campaignsToLoad.length === 0) {
      return;
    }

    const campaignData = await Promise.all(
      campaignsToLoad.map(async ({ id }) => await fetchCustomFields(id)),
    );
    dispatch(updateSelectedCampaignCustomFields(campaignData));
  };
};

export const submitDonationSuccess = ({ pendingSettlementNotice = false } = {}) => {
  clearCart(cartKey);

  return {
    type: SUBMIT_DONATION_SUCCESS,
    payload: { pendingSettlementNotice },
  };
};

export const fetchCampaignsFromApi =
  (entityId, page, limit, search, category, everydayFormsId = null, campaignIds = null) =>
  (dispatch, getState) =>
    fetchCampaigns(
      dispatch,
      getState,
      entityId,
      page,
      limit,
      search,
      category,
      everydayFormsId,
      campaignIds,
    );

const fetchCampaigns = debounce(
  async (
    dispatch,
    getState,
    entityId,
    page,
    limit,
    search,
    category,
    everydayFormId,
    campaignIds,
  ) => {
    // Read before the await: a reset during the cart check also makes this stale.
    const listVersion = getState().givingFormV2.campaignListVersion;
    dispatch(setLoading());
    const cart = fetchCart(cartKey);

    if (cart.some((item) => item.entityId && item.entityId !== entityId)) {
      // clear cart if any of the campaigns have the wrong entityId
      dispatch(resetCampaignSelections());
      clearCart(cartKey);
    } else {
      // remove closed campaigns from cart
      await axios
        .post(`${CAMPAIGNS_STATUS_API}`, { campaign_ids: cart.map((item) => item.id) })
        .then((response) => {
          const activeCampaignIds = response.data.active_campaign_ids;
          cart.forEach((cartItem) => {
            if (!activeCampaignIds.includes(cartItem.id)) {
              dispatch(removeSelectedCampaign(cartItem.id));
            }
          });
        });
    }

    const payload = {
      entity_id: entityId,
      page,
      limit,
      search,
      category,
      everydayFormId,
      ids: campaignIds,
    };

    axios
      .post(CAMPAIGNS_LIST_API, payload)
      .then((response) => {
        if (getState().givingFormV2.campaignListVersion !== listVersion) {
          return;
        }
        dispatch(fetchSuccess(response));
        dispatch(nextPage());
      })
      .catch((error) => dispatch(fetchError(error)))
      .finally(() => dispatch(setLoadingDone()));
  },
  400,
);

const fetchCustomFields = async (campaignId) => {
  return getCampaignCustomFields(campaignId);
};

export const submitMatchingGift = (formData) => {
  return async (dispatch) => {
    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    const token = document.querySelector("[name=csrf-token]").content;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = token;

    dispatch(setLoading());
    axios
      .post(MATCHING_GIFTS_API, formData, config)
      .then((response) => {
        if (response.data.email) {
          MixpanelService.identify(response.data.email);
        }
        dispatch(setGivingStep("givingThankyouStep"));
        dispatch(submitDonationSuccess(response));
      })
      .catch((error) => {
        dispatch(submitDonationError(error.response.data.errors));
      });
  };
};

export const submitDonationApiSubscription = (formData) => {
  return async (dispatch) => {
    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    const token = document.querySelector("[name=csrf-token]").content;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = token;

    dispatch(setLoading());
    axios
      .post(DONATIONS_V2_API, formData, config)
      .then((response) => {
        console.log("subscription donation success");
        if (response.data.subscription_client_secret === "paid") {
          dispatch(setLoadingDone());
          dispatch(setGivingStep("givingPaymentStep"));
        } else {
          dispatch(setStripeClientSecret(response.data.subscription_client_secret));
          dispatch(setLoadingDone());
          dispatch(setGivingStep("givingPaymentStep"));
        }

        if (response.data.payment_id) {
          dispatch(setPaymentId(response.data.payment_id));
        }

        if (response.data.features_enabled.includes("double_donation")) {
          dispatch(setDoubleDonation(response.data.double_donation));
          saveDoubleDonation(response.data.double_donation, formData, true);
        }
      })
      .catch((error) => {
        dispatch(setGiveButtonDisable(false));
        console.log("subscription donation failure", error);
      });
  };
};

export const submitDonationApi = (formData) => {
  return async (dispatch, getState) => {
    const state = getState();
    axios.defaults.headers.common["X-CSRF-TOKEN"] =
      document.querySelector("[name=csrf-token]").content;

    dispatch(setLoading());
    return axios
      .post(
        formData.get("donations[][everyday_form_id]")
          ? DONATIONS_EVERYDAY_FORM_V2_API
          : DONATIONS_V2_API,
        formData,
        {
          headers: { "content-type": "multipart/form-data" },
        },
      )
      .then((response) => {
        if (response.data.pending_3ds_status === true) {
          // set setStart3dsFlow to the transaction_token and pass it to the 3DS2 lifecycle
          dispatch(setStart3dsFlow(response.data.transaction_token));
          dispatch(setLoadingDone());
        } else if (response.data.touchnet_url) {
          // support redirect to touchnet payment form
          const form = document.querySelector(".touchnet-form");
          form.action = response.data.touchnet_url;
          form.method = "post";
          document.querySelector(".touchnet-ticket").value =
            response.data.touchnet_ticket;
          document.querySelector(".touchnet-ticket-name").value = "amplo";
          document.querySelector(".touchnet-upay-site").value =
            response.data.upay_site_id;
          form.submit();
          setLoadingDone();
        } else if (response.data.stripe_client_secret) {
          dispatch(setStripeClientSecret(response.data.stripe_client_secret));
          dispatch(setLoadingDone());
          dispatch(setGivingStep("givingPaymentStep"));
        } else if (
          // Stripe/GravytyPay error case
          !response.data.stripe_client_secret &&
          (response.data.payment_type === "Payment::GravytyPayType" ||
            response.data.payment_type === "Payment::StripeType")
        ) {
          dispatch(setGiveButtonDisable(true));
          dispatch(submitDonationError("Something went wrong with GravytyPay."));
        } else if (response.data.payment_type === "Payment::PaypalType" && state.givingFormV2.currentStep === "givingPaymentStep") {
          if (state.givingFormV2.paymentId) {
            PaymentApi.deletePaymentAndDonations(state.givingFormV2.paymentId);
          }
          dispatch(pollPaymentPaidStatus(response.data.payment_id));
        } else {
          // Successful donation creation
          dispatch(pollPaymentPaidStatus(response.data.payment_id));
        }

        if (response.data.features_enabled.includes("double_donation")) {
          dispatch(setDoubleDonation(response.data.double_donation));
          saveDoubleDonation(
            response.data.double_donation,
            formData,
            formData.get("subscription[interval]") !== null,
          );
        }

        if (response.data.email) {
          MixpanelService.identify(response.data.email);
        }

        if (response.data.payment_id) {
          dispatch(setPaymentId(response.data.payment_id));
        }

        try {
          if (typeof gtag !== "undefined") {
            const gaData = response.data.ga_data;

            const gtagData = {
              transaction_id: gaData.payment_id,
              value: gaData.payment_amount,
              currency: gaData.currency_code,
              items: gaData.items.map((item) => ({
                item_id: item.item_id,
                price: item.price,
                quantity: item.quantity,
              })),
            };

            gtag("event", "purchase", gtagData);
          }
        } catch (error) {
          console.error("Error sending gtag event:", error);
        }
      })
      .catch((error) => {
        dispatch(setGiveButtonDisable(true));
        dispatch(setBbmsTransactionToken(""));
        dispatch(submitDonationError(error.response.data.errors));
      });
  };
};

export const pollPaymentPaidStatus = (id, options = {}) => {
  const DEFAULT_MAX_POLLING_ATTEMPTS = 20;
  const ACH_MAX_POLLING_ATTEMPTS = 8;
  const INTERVAL_MS = 3000;
  const { treatTimeoutAsPending = false } = options;
  const maxPollingAttempts = treatTimeoutAsPending
    ? ACH_MAX_POLLING_ATTEMPTS
    : DEFAULT_MAX_POLLING_ATTEMPTS;

  return async (dispatch) => {
    var i = 0;
    const interval = setInterval(async () => {
      try {
        const statusResponse = await axios.get(`/api/v2/donations/is_paid`, {
          params: new URLSearchParams({ id }),
        });

        if (i++ > maxPollingAttempts) {
          clearInterval(interval);
          dispatch(setLoadingDone());
          if (treatTimeoutAsPending) {
            dispatch(setGivingStep("givingThankyouStep"));
            dispatch(submitDonationSuccess({ pendingSettlementNotice: true }));
            return;
          }
          dispatch(
            submitDonationError(
              "Something went wrong processing your donation. Please contact support.",
            ),
          );
        } else if (statusResponse.data.paid) {
          clearInterval(interval);
          dispatch(setGivingStep("givingThankyouStep"));
          dispatch(
            submitDonationSuccess({
              pendingSettlementNotice: statusResponse.data.pending_settlement === true,
            }),
          );
          dispatch(setLoadingDone());
        }
      } catch (error) {
        clearInterval(interval);
        dispatch(
          submitDonationError(
            "Something went wrong processing your donation. Please contact support.",
          ),
        );
        dispatch(setLoadingDone());
      }
    }, INTERVAL_MS);
  };
};

export const createAbandonForm = (data) => {
  const config = {
    headers: { "content-type": "multipart/form-data" },
  };

  const formData = new FormData();
  Object.keys(data).forEach((key) => formData.append(key, data[key]));

  axios
    .post(CREATE_ABANDON_FORM_API, formData, config)
    .then(() => {
      console.log(">>> CREATE_ABANDON_FORM SUCCESS");
    })
    .catch(() => {
      console.log(">>> CREATE_ABANDON_FORM ERROR");
    });
};

export const submitSpreedlChallengeSuccess = (transactionToken) => {
  return async (dispatch) => {
    dispatch(setLoading());
    axios
      .post(SRPEEDLY_CHALLENGE_SUCCESS_API, { transaction_token: transactionToken })
      .then((response) => {
        dispatch(setGivingStep("givingThankyouStep"));
        dispatch(submitDonationSuccess(response));
      })
      .catch((error) => {
        console.log("spreedly challenge success failure", error);
      });
  };
};

// Reducer
export default function reducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case SET_CURRENT_CAMPAIGN:
      return {
        ...state,
        currentCampaign: action.payload.campaign,
      };
    case SET_EVERYDAY_FORM_CAMPAIGNS_TOTAL:
      return {
        ...state,
        everydayFormCampaignsTotal: action.payload,
        hasMoreItems: action.payload > state.campaignData.length,
      };
    case SET_SHOW_CAMPAIGN_AMOUNT_SCREEN:
      return {
        ...state,
        showCampaignAmountScreen: action.payload.bool,
      };
    case SET_SHOW_GIVING_TO_SELECTED_FUNDS_SCREEN:
      return {
        ...state,
        showGivingToSelectedFundsScreen: action.payload.bool,
      };
    case SET_START_3DS_FLOW:
      return {
        ...state,
        start3dsFlow: action.payload.bool,
      };
    case SET_CAUSES_ALLOCATION:
      return {
        ...state,
        causesAllocation: action.payload.causes,
      };
    case SET_SINGLE_GIVE:
      return {
        ...state,
        isSingleGive: action.payload.isSingleGive,
      };
    case SET_SUBSCRIPTION_VALUES:
      return {
        ...state,
        subscriptionValues: {
          isSubscription: action.payload.isSubscription,
          periodLength: action.payload.periodLength,
          interval: action.payload.interval,
        },
      };
    case SET_CATEGORY:
      return {
        ...state,
        category: action.payload.category,
      };
    case SET_MATCH_ID:
      return {
        ...state,
        matchId: action.payload.matchId,
      };
    case SET_LOADING:
      return {
        ...state,
        loading: true,
      };
    case SET_LOADING_DONE:
      return {
        ...state,
        loading: false,
      };
    case UPDATE_SEARCH:
      return {
        ...state,
        search: action.payload.searchTerm,
      };
    case FETCH_SUCCESS: {
      const newCampaigns = [...state.campaignData];

      action.payload.response.data.campaigns.map((campaign) => {
        newCampaigns.push(campaign);
      });

      const campaignMap = state.campaignData
        .concat(action.payload.response.data.campaigns)
        .reduce((acc, campaign) => {
          acc[campaign.id] = campaign;
          return acc;
        }, {});

      return {
        ...state,
        campaignData: Object.values(campaignMap),
        totalCount: action.payload.response.data.meta.total,
        hasMoreItems: action.payload.response.data.meta.has_more_items,
        campaignCategories: action.payload.response.data.campaign_categories,
        featuresEnabled: action.payload.response.data.features_enabled,
        centralizedGifts: action.payload.response.data.centralized_gifts,
      };
    }
    case SET_AMBASSADORS:
      return {
        ...state,
        ambassadors: action.payload.ambassadors,
      };
    case SET_SELECTED_AMBASSADOR:
      return {
        ...state,
        selectedAmbassador: action.payload.ambassador,
      };
    case SUBMIT_DONATION_ERROR:
      return {
        ...state,
        loading: false,
        currentStep: "givingPaymentStep",
        spreedlyToken: "",
        donationError: action.payload.error,
      };
    case SUBMIT_DONATION_SUCCESS:
      return {
        ...state,
        loading: false,
        pendingSettlementNotice: action.payload.pendingSettlementNotice,
        ...pick(initialFormValues, INITIAL_STATE),
        selectedCampaigns: [],
      };
    case NEXT_PAGE:
      return {
        ...state,
        page: (state.page += 1),
      };
    case SET_FINAL_CAMPAIGNS_LIST:
      return {
        ...state,
        finalCampaignsList: action.payload.campaigns,
      };
    case PUSH_SELECTED_CAMPAIGN:
      return {
        ...state,
        currentCampaign: action.payload.campaign,
        selectedCampaigns: [
          ...state.selectedCampaigns,
          {
            ...action.payload.campaign,
            custom_fields_loaded: action.payload.campaign.custom_fields_loaded === true,
          },
        ],
      };
    case REMOVE_SELECTED_CAMPAIGN:
      return {
        ...state,
        selectedCampaigns: state.selectedCampaigns.filter(
          (item) => item.id !== action.payload.id,
        ),
      };
    case SET_CAMPAIGN_DONATE_AMOUNT:
      return {
        ...state,
        selectedCampaigns: state.selectedCampaigns.map((campaign) =>
          campaign.id === action.payload.campaignId
            ? {
                ...campaign,
                amount: action.payload.amount,
              }
            : campaign,
        ),
      };
    case SET_ADDITIONAL_GIFT_AMOUNT:
      return {
        ...state,
        centralizedGifts: state.centralizedGifts.map((campaign) =>
          campaign.id === action.payload.campaignId
            ? {
                ...campaign,
                amount: action.payload.amount,
              }
            : campaign,
        ),
      };
    case SET_TOTAL_AMOUNT:
      return {
        ...state,
        totalAmount: action.payload.amount,
      };
    case SET_FEE_AMOUNT:
      return {
        ...state,
        feeAmount: action.payload.amount,
      };
    case RESET_CAMPAIGN_SELECTIONS:
      return {
        ...state,
        selectedCampaigns: [],
      };
    case RESET_ADDITIONAL_GIFTS:
      return {
        ...state,
        centralizedGifts: state.centralizedGifts.map((campaign) => ({
          ...campaign,
          amount: null,
        })),
      };
    case RESET_CAMPAIGN_FETCH:
      return {
        ...state,
        campaignData: [],
        page: 1,
        campaignListVersion: state.campaignListVersion + 1,
      };
    case SET_GIVING_STEP:
      return {
        ...state,
        currentStep: action.payload.step,
      };
    case SET_DONATION_LEVEL_ID:
      return {
        ...state,
        donationLevelId: action.payload.id,
      };
    case SET_IS_GIFT_AID:
      return {
        ...state,
        isGiftAid: action.payload.bool,
      };
    case SET_IS_ABANDON_FORM:
      return {
        ...state,
        isAbandonForm: action.payload.bool,
      };
    case SET_ADDED_ADDITIONAL_GIFT:
      return {
        ...state,
        currentFormObjectDonatePayment: {
          ...state.currentFormObjectDonatePayment,
          addedAdditionalGift: action.payload.bool,
        },
      };
    case SET_DONATE_PAYMENT_VALUES:
      return {
        ...state,
        currentFormObjectDonatePayment: action.payload.values,
      };
    case SET_RESOLVED_CUSTOM_FIELDS:
      return {
        ...state,
        resolvedCustomFields: action.payload.fields,
      };
    case SET_SPREEDLY_KEY:
      return {
        ...state,
        spreedlyKey: action.payload.key,
      };
    case SET_SPREEDLY_MONTH:
      return {
        ...state,
        spreedlyMonth: action.payload.month,
      };
    case SET_SPREEDLY_YEAR:
      return {
        ...state,
        spreedlyYear: action.payload.year,
      };
    case SET_STRIPE_SETUP_INTENT:
      return {
        ...state,
        stripeSetupIntent: action.payload.setupIntent,
      };
    case SET_STRIPE_CLIENT_SECRET:
      return {
        ...state,
        stripeClientSecret: action.payload.secret,
      };
    case SET_PAYMENT_SUBMIT:
      return {
        ...state,
        paymentSubmit: action.payload.step,
      };
    case SET_STRIPE_PAYMENT_INTENT:
      return {
        ...state,
        stripePaymentIntent: action.payload.paymentIntent,
      };
    case SET_BRAINTREE_PAYMENT_DATA:
      return {
        ...state,
        braintreePaymentData: action.payload.paymentData,
      };
    case SET_BRAINTREE_CLIENT_DATA:
      return {
        ...state,
        braintreeClientData: action.payload.clientData,
      };
    case SET_BBMS_CLIENT_DATA:
      return {
        ...state,
        bbmsClientData: action.payload.clientData,
      };
    case SET_BBMS_TRANSACTION_TOKEN:
      return {
        ...state,
        bbmsTransactionToken: action.payload.token,
      };
    case SET_DAF_AMOUNT:
      return {
        ...state,
        dafAmount: action.payload.amount,
      };
    case SET_DAF_WORKFLOW_SESSION_ID:
      return {
        ...state,
        dafWorkflowSessionId: action.payload.workflowSessionId,
      };
    case SET_SPREEDLY_TOKEN:
      return {
        ...state,
        spreedlyToken: action.payload.token,
      };
    case SET_DONATION_ERROR:
      return {
        ...state,
        donationError: action.payload.error,
      };
    case SET_GIVE_BUTTON_DISABLE:
      return {
        ...state,
        giveButtonDisabled: action.payload.isDisable,
      };
    case SET_DD_COMPANY_ID:
      return {
        ...state,
        dDCompanyId: action.payload.id,
      };
    case SET_DOUBLE_DONATION:
      return {
        ...state,
        doubleDonation: action.payload.dd,
      };
    case SET_PAYMENT_ID:
      return {
        ...state,
        paymentId: action.payload.paymentId,
      };
    case UPDATE_SELECTED_CAMPAIGN_CUSTOM_FIELDS:
      return {
        ...state,
        selectedCampaigns: state.selectedCampaigns.map((campaign) => {
          const matchingCampaign = action.payload.campaignData.find(
            (campaignData) => campaignData.id === campaign.id,
          );

          if (!matchingCampaign) {
            return campaign;
          }

          return {
            ...campaign,
            custom_fields: matchingCampaign.custom_fields || [],
            custom_fields_loaded: true,
          };
        }),
      };
    case FETCH_ERROR:
      return { ...state, loading: false, error: action.payload.error };
    default:
      return state;
  }
}
